import { useTranslation } from 'react-i18next';

import { TabsRouter, TabRoute } from './TabsRouter';

interface PersonnelTabsProps {
  activeTab: 'users' | 'roles';
  onTabChange: (tab: 'users' | 'roles') => void;
}

/**
 * Personnel Tabs Component
 *
 * Tabs for navigating between Users and Roles in the Personnel module.
 * Uses TabsRouter internally for route-based navigation.
 *
 * @param {PersonnelTabsProps} props - Component properties
 * @param {'users' | 'roles'} props.activeTab - Currently active tab
 * @param {Function} props.onTabChange - Callback when tab is changed
 * @returns {React.ReactElement} Personnel tabs component
 *
 * @example
 * <PersonnelTabs
 *   activeTab="roles"
 *   onTabChange={(tab) => console.log(tab)}
 * />
 */
export const PersonnelTabs = ({ activeTab, onTabChange }: PersonnelTabsProps): React.ReactElement => {
  const { t } = useTranslation('personnel');

  const tabs: TabRoute[] = [
    { value: 'users', label: t('tabs.users'), path: '/employees' },
    { value: 'roles', label: t('tabs.roles'), path: '/roles' },
  ];

  const handleTabChange = (tab: string) => {
    onTabChange(tab as 'users' | 'roles');
  };

  return (
    <div style={{ gridArea: 'tabs-navigation' }}>
      <TabsRouter activeTab={activeTab} onTabChange={handleTabChange} tabs={tabs} />
    </div>
  );
};

// Legacy export for backwards compatibility (deprecated)
/** @deprecated Use PersonnelTabs instead */
export const TabsNavigation = PersonnelTabs;
